import { IFilter } from '@epicuro-next/ataraxia/models';
import { FiltersFieldIds } from '@epicuro-next/models/order-book';
import { getUTCNow, utcDateTime } from '@epicuro-next/utilities';
import { OrderFilter } from 'graphql-api/baskets-api-interfaces';
import { DurationObject } from 'luxon';

/**
 * This function gets the list of filters loaded from backend, list of filter values set by user
 * and rule engine flag that indicates if user can filter by year (presented by Delivery Year filter on the UI)
 *
 * The output of the function is a range of dates presented as [startIsoDate, endIsoDate]
 * This satisfies the expected format of the filter value in the backend request
 *
 * 1. If user can filter by year:
 *
 *   The output range is the period of one year.
 *   For e.g 2020 = ['2020', '2021']
 *
 *   If year value can be found in active filter values, it's completely determined
 *   If year value is not set by user yet, this function returns current year (if presented in possible values)
 *   or the last one from possible values
 *
 * 2. If user can not filter by year:
 *
 *   The output range is set to the special range defined by formula
 *   - startDate = currentDate - 13 months
 *   - endDate = currentDate + 13 months
 *
 * @param availableFilters
 * @param activeFilters
 * @param filterByYearEnabled
 */

export const getYearFilterValue = (
  availableFilters: OrderFilter[],
  activeFilters: IFilter[],
  filterByYearEnabled: boolean,
): [string, string] => {
  if (!filterByYearEnabled) {
    return getDefaultDateRange();
  }
  const setByUser = activeFilters.find(isYearFilter);
  if (setByUser) {
    return getYearRange(setByUser.value);
  }

  const yearFilter = availableFilters.find(isYearFilter);
  if (yearFilter) {
    return getYearRangeFromAvailableValues(yearFilter);
  }

  return getDefaultDateRange();
};

const isYearFilter = (filter: IFilter | OrderFilter) =>
  filter.fieldId === FiltersFieldIds.DeliveryYear;

const getYearRangeFromAvailableValues = (yearFilter: OrderFilter) => {
  const years = yearFilter.values?.map((value) => value.value) ?? [];
  const currentYear = utcDateTime(getUTCNow()).year.toString();
  if (years.includes(currentYear)) {
    return getYearRange(currentYear);
  }
  if (years.length) {
    const sortedYears = [...years].sort();
    return getYearRange([...sortedYears].reverse()[0]);
  }
  return getDefaultDateRange();
};

export const getYearRange = (year: string): [string, string] => {
  return [
    utcDateTime(year).toISODate(),
    utcDateTime(year).plus({ year: 1 }).toISODate(),
  ];
};

const getDefaultDateRange = (): [string, string] => {
  // This is a hardcoded duration according to the largest configured duration
  // We expect to filter by year for all tenants
  const duration: DurationObject = { months: 13 };
  const utcToday = utcDateTime(getUTCNow());
  return [
    utcToday.minus(duration).toISODate(),
    utcToday.plus(duration).toISODate(),
  ];
};
